/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import { useAppDispatch } from 'helpers/store'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import { ModalSize } from 'types/enums/ModalSize'
import { toast } from 'utils/toastUtils'
import { TableInitBase } from 'utils/hooks/useTable'
import { orderMassActionsForBar } from './orderMassActionsForBar'
import { MassAction, MassActionContext } from './types'

interface UiTableMassActionsBarProps {
    tableInit: TableInitBase
    className?: string
}

const UiTableMassActionsBar: React.FC<UiTableMassActionsBarProps> = ({ tableInit, className }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [runningKey, setRunningKey] = React.useState<string | null>(null)

    const actions: MassAction[] = orderMassActionsForBar(tableInit.massActions ?? [])
    const selectedCount = tableInit.selectedCount ?? 0

    if (actions.length === 0 || selectedCount === 0) {
        return null
    }

    const buildContext = (): MassActionContext => {
        const ids = tableInit.selectedRowIds ?? []
        const idSet = new Set(ids.map((id) => String(id)))
        // Prefer live recordsTable so mass-action patches (tags, status, …) are visible immediately.
        // Compare as strings — offer rows use ids like "offer-123" (Number() would be NaN and match everything).
        const rowsFromTable = (tableInit.recordsTable ?? []).filter((row) =>
            idSet.has(String((row as { id?: number | string | null }).id ?? ''))
        )

        return {
            ids,
            rows: rowsFromTable.length > 0 ? rowsFromTable : (tableInit.selectedRows ?? []),
            clearSelection: () => tableInit.clearSelection?.(),
        }
    }

    const runAction = async (action: MassAction) => {
        const context = buildContext()
        setRunningKey(action.key)
        try {
            await action.onRun(context)
            if (action.keepSelectionAfterRun === false) {
                tableInit.clearSelection?.()
            }
        } catch (error) {
            console.error(`Mass action "${action.key}" failed`, error)
            toast.error(t('Mass action failed'))
        } finally {
            setRunningKey(null)
        }
    }

    const handleClick = (action: MassAction) => {
        if (!action.confirm) {
            void runAction(action)
            return
        }

        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={action.confirm.title}
                    message={action.confirm.message}
                    confirmLabel={action.confirm.confirmLabel}
                    cancelLabel={action.confirm.cancelLabel}
                    confirmStyle={action.confirm.confirmStyle ?? UiButtonStyle.DELETE}
                    onConfirm={async () => {
                        await runAction(action)
                        dispatch(closeSecondaryModal())
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                action.confirm.title
            )
        )
    }

    const isActionDisabled = (action: MassAction): boolean => {
        if (typeof action.disabled === 'function') {
            return action.disabled(buildContext())
        }
        return Boolean(action.disabled)
    }

    return (
        <div
            className={clsx(
                'UiTableMassActionsBar flex items-center gap-3 px-4 py-2 bg-blue-50 border-b border-blue-200',
                'dark:bg-blue-950/40 dark:border-blue-900',
                className
            )}
        >
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {selectedCount} {t('selected')}
            </span>
            <button
                type="button"
                onClick={() => tableInit.clearSelection?.()}
                className={clsx(
                    'inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded',
                    'text-blue-600 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-900/40'
                )}
                title={t('Clear selection') as string}
            >
                <XMarkIcon className="w-3.5 h-3.5" />
                {t('Clear selection')}
            </button>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
                {actions.map((action) => (
                    <UiButton
                        key={action.key}
                        size={UiButtonSize.Small}
                        style={action.style ?? UiButtonStyle.Secondary}
                        onClick={() => handleClick(action)}
                        title={action.title}
                        icon={action.icon}
                        loading={runningKey === action.key}
                        disabled={isActionDisabled(action) || runningKey !== null}
                    >
                        {action.label}
                    </UiButton>
                ))}
            </div>
        </div>
    )
}

export default UiTableMassActionsBar
